import React, { memo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes } from '../../components/foundations/Icon';
import css from './ChatInput.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT';

const ChatInput = memo(
    ({
        input,
        handleInputChange,
        handleSubmit,
        isLoading,
        stop,
        onMicClick,
        isMicListening = false,
        micSupported = true,
        containerClassName,
        inputRef: externalInputRef = null,
        translate,
    }) => {
        const localInputRef = useRef(null);
        const inputRef = externalInputRef || localInputRef;

        // Auto-resize the textarea up to its CSS max-height.
        const autoResize = el => {
            if (!el) return;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
        };

        useEffect(() => {
            inputRef.current?.focus();
        }, [inputRef]);

        // Re-run resize when value changes from outside (e.g. mic transcript appends).
        useEffect(() => {
            autoResize(inputRef.current);
        }, [input, inputRef]);

        const handleChange = e => {
            autoResize(e.target);
            handleInputChange(e);
        };

        const handleKeyDown = e => {
            // Submit on Enter (without Shift)
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
            }
        };

        const handleFormSubmit = e => {
            e.preventDefault();
            if (input.trim() && !isLoading) {
                handleSubmit(e);
            }
        };

        const focusInputFromWrapper = e => {
            if (e.target.closest('button, textarea, input, a')) return;
            e.preventDefault();
            inputRef.current?.focus();
        };

        const handleMicClick = () => {
            onMicClick?.();
            const el = inputRef.current;
            if (!el) return;
            const end = el.value.length;
            el.focus();
            el.setSelectionRange(end, end);
        };

        return (
            <div className={classNames(css.chatInputContainer, containerClassName)} onMouseDown={focusInputFromWrapper}>
                <form onSubmit={handleFormSubmit} className={css.chatInputForm}>
                    <div className={css.inputContainer}>
                        <div className={css.inputHeader}>
                            <textarea
                                ref={inputRef}
                                rows={1}
                                className={css.inputField}
                                placeholder={translate(
                                    isLoading
                                        ? `${TRANSLATION_PREFIX}.INPUT_PLACEHOLDER_LOADING`
                                        : `${TRANSLATION_PREFIX}.INPUT_PLACEHOLDER`
                                )}
                                value={input}
                                onChange={handleChange}
                                onKeyDown={handleKeyDown}
                                disabled={isLoading}
                            />
                            <div className={css.inputHeaderIcons}>
                                {micSupported && (
                                    <button
                                        type="button"
                                        className={classNames(css.iconButton, {
                                            [css.micListening]: isMicListening,
                                        })}
                                        onClick={handleMicClick}
                                        disabled={isLoading}
                                        aria-label={translate(
                                            isMicListening
                                                ? `${TRANSLATION_PREFIX}.MIC_STOP_LISTENING_LABEL`
                                                : `${TRANSLATION_PREFIX}.MIC_LABEL`
                                        )}
                                        title={translate(
                                            isMicListening
                                                ? `${TRANSLATION_PREFIX}.MIC_STOP_LISTENING_LABEL`
                                                : `${TRANSLATION_PREFIX}.MIC_LABEL`
                                        )}
                                        aria-pressed={isMicListening}
                                    >
                                        <Icon
                                            name={isMicListening ? 'mic-fill' : 'mic-line'}
                                            type="mingcute"
                                            disabled={isLoading}
                                            size={IconSizes.SMALL}
                                        />
                                    </button>
                                )}
                                {isLoading ? (
                                    <button
                                        type="button"
                                        className={classNames(css.iconButton, css.sendButton)}
                                        onClick={stop}
                                        aria-label={translate(`${TRANSLATION_PREFIX}.STOP_LABEL`)}
                                        title={translate(`${TRANSLATION_PREFIX}.STOP_LABEL`)}
                                    >
                                        <Icon name="square" type={IconTypes.TABLER} size={IconSizes.SMALL} />
                                    </button>
                                ) : (
                                    <button
                                        type="submit"
                                        disabled={!input.trim() || isLoading}
                                        className={classNames(css.iconButton, css.sendButton)}
                                        aria-label={translate(`${TRANSLATION_PREFIX}.SEND_LABEL`)}
                                        title={translate(`${TRANSLATION_PREFIX}.SEND_LABEL`)}
                                    >
                                        <Icon
                                            name="arrow-up-fill"
                                            type="mingcute"
                                            size={IconSizes.SMALL}
                                            interactive={false}
                                        />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        );
    }
);

ChatInput.propTypes = {
    input: PropTypes.string.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    stop: PropTypes.func.isRequired,
    onMicClick: PropTypes.func,
    isMicListening: PropTypes.bool,
    micSupported: PropTypes.bool,
    containerClassName: PropTypes.string,
    inputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ChatInput);
